import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { formatDay } from '../format'
import { advancedColors } from '../theme'
import DragHandle from '../dashboard/DragHandle'
import {
  ASSIGNABLE_TAB_ICONS,
  TAB_ICONS,
  TabDefinition,
  TabIconKey,
  isDefaultTab,
  isPermanentTab,
} from '../dashboard/tabs'
import DialogHeader from './DialogHeader'

export type TabWithContent = TabDefinition & { hasContent: boolean }

type Props = {
  tabs: TabWithContent[]
  open: boolean
  onClose: () => void
  onAddTab: (label: string, icon: TabIconKey) => void
  onRemoveTab: (id: string) => void
  onSetVisible: (id: string, visible: boolean) => void
  onMoveTo: (from: number, to: number) => void
}

const ContentIndicator = ({ hasContent }: { hasContent: boolean }) => (
  <Chip
    size="small"
    label={hasContent ? 'Has content' : 'Empty'}
    sx={
      hasContent
        ? { backgroundColor: advancedColors.accentSoft, color: advancedColors.green }
        : { backgroundColor: advancedColors.line, color: advancedColors.inkMuted }
    }
  />
)

const TabRow = ({
  tab,
  onRemove,
  onSetVisible,
}: {
  tab: TabWithContent
  onRemove: () => void
  onSetVisible: (visible: boolean) => void
}) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: tab.id })
  const Icon = TAB_ICONS[tab.icon]
  const permanent = isPermanentTab(tab.id)
  const isDefault = isDefaultTab(tab.id)

  let visibilityTooltip = 'Show on the bottom bar'
  if (permanent) {
    visibilityTooltip = 'Always on the bottom bar'
  } else if (tab.visible) {
    visibilityTooltip = 'Hide from the bottom bar'
  }

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
    >
      <DragHandle attributes={attributes} listeners={listeners} label={`Reorder — ${tab.label}`} />
      <Icon />
      <Typography variant="body1" sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} noWrap>
        {tab.label}
      </Typography>
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted, whiteSpace: 'nowrap' }}>
        {formatDay(tab.createdAt)} · {tab.createdBy}
      </Typography>
      <ContentIndicator hasContent={tab.hasContent} />
      <Tooltip title={visibilityTooltip}>
        <span>
          <IconButton
            size="small"
            aria-label={tab.visible ? 'Hide tab' : 'Show tab'}
            onClick={() => onSetVisible(!tab.visible)}
            disabled={permanent}
          >
            {tab.visible ? <VisibilityRoundedIcon fontSize="small" /> : <VisibilityOffRoundedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={isDefault ? "Can't be deleted" : 'Delete tab'}>
        <span>
          <IconButton size="small" aria-label={`Delete ${tab.label}`} onClick={onRemove} disabled={isDefault}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

const AddTabRow = ({ onAddTab }: { onAddTab: (label: string, icon: TabIconKey) => void }) => {
  const [label, setLabel] = useState('')
  const [icon, setIcon] = useState<TabIconKey>(ASSIGNABLE_TAB_ICONS[0])

  const submit = () => {
    if (!label.trim()) {
      return
    }
    onAddTab(label, icon)
    setLabel('')
    setIcon(ASSIGNABLE_TAB_ICONS[0])
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
      <Typography variant="overline">Add a tab</Typography>
      <TextField
        size="small"
        label="Tab name"
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && submit()}
        fullWidth
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          value={icon}
          onChange={(_event, value: TabIconKey | null) => value && setIcon(value)}
        >
          {ASSIGNABLE_TAB_ICONS.map((key) => {
            const Icon = TAB_ICONS[key]

            return (
              <ToggleButton key={key} value={key} aria-label={key}>
                <Icon />
              </ToggleButton>
            )
          })}
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          disabled={!label.trim()}
          onClick={submit}
          sx={{ ml: 'auto' }}
        >
          Add tab
        </Button>
      </Box>
    </Box>
  )
}

const ManageTabsDialog = ({ tabs, open, onClose, onAddTab, onRemoveTab, onSetVisible, onMoveTo }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const [pendingDeletion, setPendingDeletion] = useState<TabWithContent | null>(null)

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return
    }
    const from = tabs.findIndex((tab) => tab.id === active.id)
    const to = tabs.findIndex((tab) => tab.id === over.id)
    if (from !== -1 && to !== -1) {
      onMoveTo(from, to)
    }
  }

  const confirmDeletion = () => {
    if (pendingDeletion) {
      onRemoveTab(pendingDeletion.id)
    }
    setPendingDeletion(null)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogHeader title="Manage tabs" onClose={onClose} />
        <DialogContent>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={tabs.map((tab) => tab.id)} strategy={verticalListSortingStrategy}>
              {tabs.map((tab) => (
                <TabRow
                  key={tab.id}
                  tab={tab}
                  onRemove={() => setPendingDeletion(tab)}
                  onSetVisible={(visible) => onSetVisible(tab.id, visible)}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Divider sx={{ mt: 1 }} />
          <AddTabRow onAddTab={onAddTab} />
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDeletion !== null} onClose={() => setPendingDeletion(null)} maxWidth="xs" fullWidth>
        <DialogHeader title={`Delete "${pendingDeletion?.label}"?`} onClose={() => setPendingDeletion(null)} />
        <DialogContent>
          <DialogContentText>
            This can&apos;t be undone{pendingDeletion?.hasContent ? ' and its content will be lost' : ''}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeletion(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeletion}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ManageTabsDialog
